import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; 
import { fetchQuestions, submitAnswersBatch, saveProgress, deletePreviousAssessment, fetchProgress } from '../../api/assessmentApi'; 
import { v4 as uuidv4 } from 'uuid';
import './Assessment.css';
import {
  FaSave,
} from "react-icons/fa";
import confetti from 'canvas-confetti';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// Utility to shuffle an array
const shuffle = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Remove console.logs in production
  if (process.env.NODE_ENV !== "development") {
    console.log = () => {};
  }

// Custom hook for managing local storage
const useLocalStorage = (userId) => {
  const getStorageKey = (key) => `careerAssessment${key}_${userId}`;

  const getStoredData = (key, defaultValue = null) => {
    const value = localStorage.getItem(getStorageKey(key));
    if (!value) {
      console.log(`No data found for key "${getStorageKey(key)}"`);
      return defaultValue;
    }
    try {
      return JSON.parse(value);
    } catch (err) {
      console.error(`Failed to parse JSON for key "${getStorageKey(key)}":`, err);
      console.error(`Stored value:`, value);
      return defaultValue;
    }
  };

  const setStoredData = (key, value) => {
    try {
      const serialized = JSON.stringify(value);
      localStorage.setItem(getStorageKey(key), serialized);
      console.log(`Stored ${key} in localStorage:`, serialized);
    } catch (err) {
      console.error(`Failed to store data for key "${getStorageKey(key)}":`, err);
      throw err;
    }
  };

  const removeStoredData = (key) => {
    console.log(`Removing ${key} from localStorage`);
    localStorage.removeItem(getStorageKey(key));
  };

  return { getStoredData, setStoredData, removeStoredData };
};

const CareerAssessment = ({ userId, isAdmin = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getStoredData, setStoredData, removeStoredData } = useLocalStorage(userId);
  const [allQuestions, setAllQuestions] = useState([]);
  const [currentAllIndex, setCurrentAllIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [answeredOrder, setAnsweredOrder] = useState([]);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [answeredCountByCategory, setAnsweredCountByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastCompletionDate, setLastCompletionDate] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [isExampleOpen, setIsExampleOpen] = useState(false);
  const [submittedAnswers, setSubmittedAnswers] = useState(new Set());
const [showIntro, setShowIntro] = useState(true);
const [showMidCelebration, setShowMidCelebration] = useState(false);
const [midCelebrationShown, setMidCelebrationShown] = useState(false);
const [answeredCountBySubCategory, setAnsweredCountBySubCategory] = useState({});

  // Track page views when the component mounts or route changes
  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-B8RJQ1KWE4', {
        page_path: location.pathname,
        page_title: 'Career Assessment',
      });
    }
  }, [location]);



useEffect(() => {
  if (showIntro || showMidCelebration) {
    document.body.classList.add('specific-page-intro-screen');
  } else {
    document.body.classList.remove('specific-page-intro-screen');
  }

  return () => document.body.classList.remove('specific-page-intro-screen');
}, [showIntro, showMidCelebration]);

useEffect(() => {
  if (midCelebrationShown || showMidCelebration) return;

  // Use the new sub-category-specific counter
  const completedSubCats = Object.entries(answeredCountBySubCategory)
    .filter(([subCat, answeredCount]) => {
      // Count total questions in this sub-category
      const totalQuestionsInSubCat = allQuestions.filter(
        q => q.subCategory === subCat
      ).length;

      return answeredCount === totalQuestionsInSubCat && totalQuestionsInSubCat > 0;
    })
    .map(([subCat]) => subCat);

  console.log("Completed sub-categories so far:", completedSubCats); // ← helpful debug

  if (completedSubCats.length === 2) {
    console.log("Exactly 2 sub-categories completed → triggering celebration");
    setShowMidCelebration(true);
    setMidCelebrationShown(true);

    // Fireworks animation
    const shootFireworks = () => {
      confetti({
        particleCount: 12,
        angle: Math.random() * 360,
        spread: 75,
        startVelocity: 50 + Math.random() * 30,
        decay: 0.92,
        gravity: 0.65,
        ticks: 300,
        origin: { x: Math.random(), y: Math.random() * 0.3 },
        colors: ['#ff0044', '#00ff88', '#4488ff', '#ffff44', '#ff44ff', '#00ffff', '#ffffff', '#ff8800'],
        shapes: ['circle', 'star'],
        scalar: 1.3,
      });
    };

    shootFireworks();
    setTimeout(shootFireworks, 250);
    setTimeout(shootFireworks, 500);
    setTimeout(shootFireworks, 750);
    setTimeout(shootFireworks, 1000);
    setTimeout(shootFireworks, 1250);
  }
}, [
  answeredCountBySubCategory,   // ← key dependency
  allQuestions,
  midCelebrationShown,
  showMidCelebration
]);

  const canTakeAssessment = () => {
    if (isAdmin) return true;
    if (!lastCompletionDate) return true;
    try {
      const sixMonthsInMs = 6 * 30 * 24 * 60 * 60 * 1000;
      const timeSinceLast = Date.now() - new Date(lastCompletionDate).getTime();
      return timeSinceLast >= sixMonthsInMs;
    } catch (err) {
      console.error('Invalid lastCompletionDate:', lastCompletionDate);
      return true;
    }
  };

const isValidsubCategoryOrder = (questions) => {
  let lastsubCategory = null;
  for (let i = 0; i < questions.length; i++) {
    const currentsubCategory = questions[i].subCategory || 'Uncategorized';
    if (lastsubCategory && lastsubCategory !== currentsubCategory) {
      // Check remaining questions for any that match lastsubCategory
      for (let j = i + 1; j < questions.length; j++) {
        if ((questions[j].subCategory || 'Uncategorized') === lastsubCategory) {
          console.warn(`Invalid order: ${lastsubCategory} mixed with ${currentsubCategory}`);
          return false;
        }
      }
    }
    lastsubCategory = currentsubCategory;
  }
  return true;
};

  useEffect(() => {
    let mounted = true;

    const loadAssessment = async () => {
      if (!mounted) return;

      const storedCompletion = getStoredData('Completion');
      let storedSessionId = getStoredData('SessionId');
      let initialAnswers = getStoredData('Answers', {});
      let initialOrder = getStoredData('Order', []);
      let initialQuestions = getStoredData('Questions', []);
      let initialIndex = getStoredData('Index', 0);
      let newSession = false;

      console.log('Initial stored data:', {
        storedCompletion,
        storedSessionId,
        answersCount: Object.keys(initialAnswers).length,
        orderLength: initialOrder.length,
        questionsLength: initialQuestions.length,
        initialIndex
      });

      setLastCompletionDate(storedCompletion);

      const canRetake = canTakeAssessment();
      const hasLocalProgress =
        storedSessionId &&
        Object.keys(initialAnswers).length > 0 &&
        Array.isArray(initialOrder) &&
        Array.isArray(initialQuestions);

      console.log('Session checks:', { isAdmin, canRetake, storedCompletion, hasLocalProgress });

      // Only start a new session for admins if there's no incomplete progress
      // or for non-admins if the assessment is completed and can be retaken
      if ((isAdmin && !hasLocalProgress) || (!isAdmin && canRetake && storedCompletion)) {
        console.log('Starting new session: admin with no progress or completed assessment');
        storedSessionId = uuidv4();
        newSession = true;
        removeStoredData('Answers');
        removeStoredData('Order');
        removeStoredData('Questions');
        removeStoredData('Index');
        removeStoredData('SessionId');
        removeStoredData('Completion');
        initialAnswers = {};
        initialOrder = [];
        initialQuestions = [];
        initialIndex = 0;
      } else if (hasLocalProgress && !storedCompletion) {
        console.log('Resuming existing incomplete session');
        newSession = false;
      } else {
        console.log('No valid local progress or starting new session');
        storedSessionId = uuidv4();
        newSession = true;
        removeStoredData('Answers');
        removeStoredData('Order');
        removeStoredData('Questions');
        removeStoredData('Index');
        removeStoredData('SessionId');
        initialAnswers = {};
        initialOrder = [];
        initialQuestions = [];
        initialIndex = 0;
      }

      try {
        const data = await fetchQuestions({ status: 'Career Assessments' });
        console.log('Raw questions fetched:', data);
        if (!data || data.length === 0) {
          throw new Error('No questions returned from the server.');
        }

        const uniqueQuestions = Array.from(new Map(data.map((q) => [q._id, q])).values());
        console.log('Unique questions:', uniqueQuestions);
        const validQuestions = uniqueQuestions.map((q) => ({
          ...q,
          _id: q._id || `temp-${uuidv4()}`,
          questionText: q.questionText || 'Unnamed Question',
          category: q.category || 'Uncategorized',
          subCategory: q.subCategory || 'Uncategorized',
        }));
        console.log('Valid questions after processing:', validQuestions);
        if (validQuestions.length === 0) {
          throw new Error('No valid questions available. Please contact support.');
        }

        let questionsToSet = [];
        let resetProgress = newSession;

        if (!newSession && hasLocalProgress) {
          const storedQuestionsMatch = initialQuestions.every((id) =>
            validQuestions.some((q) => q._id === id)
          );
          if (storedQuestionsMatch && initialQuestions.length === validQuestions.length) {
            questionsToSet = initialQuestions
              .map((id) => validQuestions.find((q) => q._id === id))
              .filter((q) => q);
            if (isValidsubCategoryOrder(questionsToSet)) {
              resetProgress = false;
            } else {
              console.log('Stored questions violate subCategory grouping, starting new session');
              resetProgress = true;
              storedSessionId = uuidv4();
            }
          } else {
            console.log('Stored questions invalid or mismatched, starting new session');
            resetProgress = true;
            storedSessionId = uuidv4();
          }
        }

        if (resetProgress) {
          const groupedByCategoryAndsubCategory = validQuestions.reduce((acc, question) => {
            const category = question.category;
            const subCategory = question.subCategory;
            if (!acc[category]) acc[category] = {};
            if (!acc[category][subCategory]) acc[category][subCategory] = [];
            acc[category][subCategory].push(question);
            return acc;
          }, {});

          const categories = Object.keys(groupedByCategoryAndsubCategory);
          const shuffledCategories = shuffle(categories);

          questionsToSet = shuffledCategories.reduce((acc, category) => {
            const subCategories = Object.keys(groupedByCategoryAndsubCategory[category]);
            const shuffledsubCategories = shuffle(subCategories);

            const categoryQuestions = shuffledsubCategories.reduce((subAcc, subCategory) => {
              const shuffledQuestions = shuffle([...groupedByCategoryAndsubCategory[category][subCategory]]);
              return [...subAcc, ...shuffledQuestions];
            }, []);

            return [...acc, ...categoryQuestions];
          }, []);

          initialQuestions = questionsToSet.map((q) => q._id);
          initialOrder = [];
          initialAnswers = {};
          initialIndex = 0;
        }

        if (mounted) {
          setSessionId(storedSessionId);
          setAllQuestions(questionsToSet);
          setSelectedAnswers(initialAnswers);
          const filteredOrder = initialOrder.filter((id) => questionsToSet.some((q) => q._id === id));
          setAnsweredOrder(filteredOrder);
          const validAnswerCount = Object.keys(initialAnswers).filter((id) =>
            questionsToSet.some((q) => q._id === id)
          ).length;
          setAnsweredCount(validAnswerCount);

          const startIndex = Math.min(Math.max(initialIndex, 0), questionsToSet.length - 1);
          setCurrentAllIndex(startIndex);

          const categoryCounts = {};
          questionsToSet.forEach((q) => {
            if (initialAnswers[q._id]) {
              categoryCounts[q.category] = (categoryCounts[q.category] || 0) + 1;
            }
          });
          setAnsweredCountByCategory(categoryCounts);

          setStoredData('SessionId', storedSessionId);
          setStoredData('Questions', questionsToSet.map((q) => q._id));
          setStoredData('Order', filteredOrder);
          setStoredData('Index', startIndex);
          setStoredData('Answers', initialAnswers);

           const introKey = `IntroShown_${storedSessionId}`;
          const introAlreadyShown = getStoredData(introKey, false);

          const shouldShowIntro = 
            Object.keys(initialAnswers).length === 0 && 
            !introAlreadyShown;

          setShowIntro(shouldShowIntro);

          console.log("Should show intro?", shouldShowIntro, {
            hasAnswers: Object.keys(initialAnswers).length > 0,
            introFlag: introAlreadyShown,
            sessionId: storedSessionId
          });

          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading questions:', err);
        if (mounted) {
          setError(`Failed to load assessment: ${err.message}. Please contact support.`);
          setLoading(false);
        }
      }
    };

    loadAssessment();
    document.body.classList.add('specific-page-career');
    return () => {
      mounted = false;
      document.body.classList.remove('specific-page-career');
    };
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isAdmin, navigate]);

  useEffect(() => {
    setIsExampleOpen(false);
  }, [currentAllIndex]);

    const handleSaveProgress = () => {
    if (!userId || !sessionId) {
      alert('User ID or session ID is missing. Please log in.');
      return;
    }

    if (answeredCount === 0) {
      console.log('No answers to save');
      alert('No answers provided to save.');
      return;
    }

    try {
      // Save progress only to local storage
      const answersToSave = { ...selectedAnswers };
      Object.keys(answersToSave).forEach((key) => {
        if (typeof answersToSave[key] !== 'string' && typeof answersToSave[key] !== 'number') {
          console.warn(`Invalid answer for question ${key}:`, answersToSave[key]);
          answersToSave[key] = String(answersToSave[key]);
        }
      });

      setStoredData('SessionId', sessionId);
      setStoredData('Answers', answersToSave);
      setStoredData('Order', answeredOrder);
      setStoredData('Questions', allQuestions.map((q) => q._id));
      setStoredData('Index', currentAllIndex);

      console.log('Progress saved locally:', {
        sessionId,
        answersCount: Object.keys(answersToSave).length,
        orderLength: answeredOrder.length,
        questionsLength: allQuestions.length,
        currentIndex: currentAllIndex,
      });

      alert('Progress saved locally!');
      setError('');
    } catch (err) {
      console.error('Error saving progress locally:', err);
      alert(`Failed to save progress: ${err.message}`);
    }
  };

  const handleContinueAfterMid = () => {
  setShowMidCelebration(false);
  handleSaveProgress(); // optional
};

const handleSaveExitFromMid = () => {
  handleSaveProgress();
  navigate('/CareerDashboard', { state: { fromCareerScores: true } });
};

  const handleReturnToDashboard = () => {
    if (answeredCount > 0) {
      handleSaveProgress();
    }
    navigate('/CareerDashboard', { state: { fromCareerScores: true } });
  };

 // Submit all answers
const handleAnswerSubmit = async () => {
  const allAnswered = allQuestions.every((q) => selectedAnswers[q._id] !== undefined && selectedAnswers[q._id] !== '');
  if (!allAnswered) {
    alert('Please complete all questions before submitting.');
    return;
  }

  const warningMessage = isAdmin
    ? 'FutureReady Assessment completed — thank you! Retake anytime.'
    : 'FutureReady Assessment completed — thank you! Retake available in 6 months.';
  alert(warningMessage);

  if (!isAdmin) {
    const confirmationMessage = 'Finalise submission? This action cannot be undone.';
    const confirmSubmit = window.confirm(confirmationMessage);
    if (!confirmSubmit) return;
  }

  // Track the "Submit" event in Google Analytics
  if (window.gtag) {
    window.gtag('event', 'submit_assessment', {
      event_category: 'CareerAssessment',
      event_label: 'Assessment Submission',
      value: answeredCount, // Number of questions answered
      session_id: sessionId, // Custom parameter for session ID
      user_id: userId, // Custom parameter for user ID (ensure compliance with privacy policies)
    });
  }

  try {
    if (!userId) {
      throw new Error('User ID is missing. Please log in.');
    }
    if (!sessionId) {
      throw new Error('Session ID is missing. Please restart the assessment.');
    }

    console.log('Submitting answers with:', { userId, sessionId, assessmentType: 'Career', answersCount: allQuestions.length });

    // Step 1: Check for prior completion
    let hasPriorData = false;
    try {
      const progress = await fetchProgress(userId, { assessmentType: 'Career', fetchLatest: true });
      hasPriorData = progress && progress.isCompleted;
      console.log('Prior progress check:', { hasPriorData, lastCompletionDate: progress?.lastCompletionDate });
    } catch (err) {
      console.warn('Error checking prior progress:', err.message);
    }

    // Step 2: Delete previous assessment data if it exists
    if (hasPriorData || isAdmin) {
      try {
        const deleteResult = await deletePreviousAssessment(userId, 'Career');
        console.log('Delete result:', deleteResult);
        if (deleteResult.message === 'No previous assessment data found') {
          if (hasPriorData) {
            console.error('Unexpected: No data deleted despite prior data existing');
            throw new Error('Failed to delete prior data despite existing records');
          }
          console.log('No previous assessment data found, proceeding with submission');
        } else if (deleteResult.message === 'Previous assessment data deleted successfully') {
          console.log('Previous assessment data deleted:', {
            userId,
            deletedAnswers: deleteResult.deletedAnswers,
            deletedProgresses: deleteResult.deletedProgresses,
          });
        } else {
          throw new Error('Unexpected deletion response: ' + JSON.stringify(deleteResult));
        }
      } catch (err) {
        console.error('Error deleting previous assessment data:', err);
        throw new Error('Failed to clear previous assessment data: ' + err.message);
      }
    } else {
      console.log('First attempt detected, no deletion needed');
    }

    // Step 3: Prepare and submit new answers
    const questionOptionsMap = allQuestions.reduce((map, q) => ({
      ...map,
      [q._id]: { options: q.options },
    }), {});
    const answersBatch = allQuestions
      .filter((question) => !submittedAnswers.has(question._id))
      .map((question) => {
        const answer = selectedAnswers[question._id];
        if (!answer) {
          throw new Error(`No answer provided for question ${question._id}`);
        }
const selectedOption = Array.isArray(question.options)
  ? question.options.find((opt) => opt.optionText === answer)
  : null;
const score = selectedOption ? selectedOption.score : 0;
        return {
          userId,
          questionId: question._id,
          sessionId,
          category: question.category,
          subCategory: question.subCategory,
          facet: question.facet,
          taxonomy: question.taxonomy || [],
          score,
          assessmentType: 'Career',
        };
      });

    if (answersBatch.length > 0) {
      await submitAnswersBatch(userId, sessionId, answersBatch, 'Career', questionOptionsMap);
      answersBatch.forEach((answer) => {
        setSubmittedAnswers((prev) => new Set(prev).add(answer.questionId));
      });
    } else {
      console.log('No new answers to submit. All questions may already be submitted.');
    }

    // Step 4: Save completion data to database
    const completionDate = new Date().toISOString();
    const progressData = {
      userId,
      sessionId,
      assessmentType: 'Career',
      lastCompletionDate: completionDate,
    };

    console.log('Saving completion data:', progressData);
    await saveProgress(progressData);

    // Step 5: Clear all local storage and update with new completion data
    removeStoredData('Answers');
    removeStoredData('Order');
    removeStoredData('Questions');
    removeStoredData('Index');
    removeStoredData('SessionId');
    removeStoredData('Completion');
    setStoredData('Completion', completionDate);

  // ─── CRITICAL: Clear ALL career-related caches to force fresh fetch on dashboard ───
    localStorage.removeItem(`raw_answers_${userId}_Career`);
    localStorage.removeItem(`progress_${userId}_Career`);
    localStorage.removeItem(`raw_answers_${userId}_career`);   // sometimes lowercased in code
    localStorage.removeItem(`progress_${userId}_career`);
    localStorage.removeItem("careerSessionId");                // ← this one is important!

    // Clear sessionStorage cache for career visualizations
    sessionStorage.removeItem(`visualizations_${userId}_Career`);
    sessionStorage.removeItem(`raw_answers_${userId}_Career`);

    navigate('/CareerDashboard', { 
  state: { 
    justSubmittedFutureReady: true, 
    justSubmittedCareer: true, 
    completionDate, 
    sessionId: sessionId,               // ← this is the new one!
    forceRefresh: true                  // optional flag
  } 
});
  } catch (err) {
    console.error('Error submitting answers:', {
      message: err.message,
      stack: err.stack,
    });
    const errorMessage =
      err.message.includes('Answer already submitted') ||
      err.message.includes('You can only complete this assessment once every 6 months')
        ? 'This assessment has already been completed. You can retake it in 6 months.'
        : err.message.includes('log in') || err.message.includes('User ID is missing')
        ? 'Session expired or invalid credentials. Please log in again.'
        : err.message.includes('Failed to clear previous assessment data')
        ? 'Unable to connect to the server to clear previous data. Please check your connection and try again.'
        : `Failed to submit answers: ${err.message}`;

            // Track submission failure
    if (window.gtag) {
      window.gtag('event', 'submit_assessment_failure', {
        event_category: 'CareerAssessment',
        event_label: 'Assessment Submission Failed',
        value: answeredCount,
        session_id: sessionId,
        user_id: userId,
        error_message: err.message,
      });
    }

    alert(errorMessage);
    if (err.message.includes('log in') || err.message.includes('User ID is missing')) {
      navigate('/login');
    } else {
      navigate('/CareerDashboard');
    }
  }
};

  const handleNext = () => {
    const currentQuestionId = allQuestions[currentAllIndex]?._id;
    const currentAnswer = selectedAnswers[currentQuestionId];

    if (!currentQuestionId || currentAnswer === undefined || currentAnswer === '') {
      alert('Please select an answer before proceeding to the next question.');
      return;
    }

    if (currentAllIndex < allQuestions.length - 1) {
      setCurrentAllIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentAllIndex === 0) {
      return;
    }
    setCurrentAllIndex((prev) => Math.max(prev - 1, 0));
  };

   const handleAnswerSelect = (answer) => {
    const currentQuestion = allQuestions[currentAllIndex];
    if (!currentQuestion) return;

    const wasPreviouslyAnswered = !!selectedAnswers[currentQuestion._id];

    setSelectedAnswers((prev) => ({
      ...prev,
      [currentQuestion._id]: answer,
    }));

    if (!wasPreviouslyAnswered) {
  setAnsweredCount((prev) => prev + 1);
  setAnsweredOrder((prev) => {
    if (!prev.includes(currentQuestion._id)) {
      return [...prev, currentQuestion._id];
    }
    return prev;
  });

  // Keep original category tracking (for your other logic)
  setAnsweredCountByCategory((prev) => ({
    ...prev,
    [currentQuestion.category]: (prev[currentQuestion.category] || 0) + 1,
  }));

  // NEW: separate tracking for sub-category (used only for celebration trigger)
  setAnsweredCountBySubCategory((prev) => ({
    ...prev,
    [currentQuestion.subCategory]: (prev[currentQuestion.subCategory] || 0) + 1,
  }));
}

    // Save progress to local storage
    try {
      setStoredData('Answers', { ...selectedAnswers, [currentQuestion._id]: answer });
      setStoredData('Order', answeredOrder.includes(currentQuestion._id) ? answeredOrder : [...answeredOrder, currentQuestion._id]);
      setStoredData('Index', currentAllIndex);
    } catch (err) {
      console.error('Error saving answer to local storage:', err);
    }

    // Auto-advance to the next question after a brief pause so the
    // selection highlight is visible before the view moves on. Skipped on
    // the last question so the Submit button shows instead. Note: this
    // does not fire while showMidCelebration is about to be triggered by
    // the useEffect above — that state swap happens on the next render
    // and simply replaces the visible screen, which is fine since the
    // mid-celebration page fully overrides the question view.
    if (currentAllIndex < allQuestions.length - 1) {
      setTimeout(() => {
        setCurrentAllIndex((prevIndex) => prevIndex + 1);
      }, 300);
    }
  };

  const toggleExampleDropdown = () => {
    setIsExampleOpen((prev) => !prev);
  };

  if (loading) return <p>Loading FutureReady Assessment questions...</p>;
  if (error) return <p className="error-message">{error}</p>;
  if (!allQuestions.length) {
    return <p className="error-message">No FutureReady Assessment questions available. Please contact support.</p>;
  }

  const currentQuestion = allQuestions[currentAllIndex];
  const currentSelectedAnswer = selectedAnswers[currentQuestion._id] || '';
  const unansweredCount = allQuestions.filter((q) => !selectedAnswers[q._id]).length;

  const showPreviousButton = currentAllIndex > 0;

  if (showIntro) {
  return (
    <div style={{marginTop: '200px'}} className="specific-page-intro-screen">
      <div className="intro-content">
        <div className="admin-dashboard-logo intro-screen logo-career"></div>

        <h2 className="intro-heading">
          Welcome to the ASPIRE FutureReady Psychometric Test
        </h2>

        <p className="copyright intro-copyright">
          © 2026 Oxbridge Gateway Ltd., All Rights Reserved.
        </p>

        <p className="intro-text intro-text-career">
          Most students complete this assessment in about <strong>10–15 minutes</strong>.<br />
          <h3 style={{marginTop: '10px'}}>You can exit at any time during the assessment — your progress will be automatically saved locally.</h3></p>

  <button
  className="start-assessment-button nav-button"
  onClick={() => {
    setShowIntro(false);
    if (sessionId) {
      const introKey = `IntroShown_${sessionId}`;
      setStoredData(introKey, true);
      console.log("Intro flag saved for session:", sessionId); // ← optional debug
    }
  }}
>
  Start Assessment
</button>
      </div>
    </div>
  );
}

if (showMidCelebration) {
  // Identify the two completed sub-categories — use the RIGHT counter!
  const completedSubCats = Object.entries(answeredCountBySubCategory)
    .filter(([sub, cnt]) => {
      const subQuestions = allQuestions.filter(q => q.subCategory === sub);
      return cnt === subQuestions.length && subQuestions.length > 0;
    })
    .map(([sub]) => sub);

  // Debug: see what we actually detected
  console.log("Completed sub-categories in celebration render:", completedSubCats);

  const [sub1, sub2] = completedSubCats;

  // Collect facet-level data for each sub-category
  const facetData1 = {};
  const facetData2 = {};

  allQuestions.forEach(q => {
    if (selectedAnswers[q._id] !== undefined) {
      const opt = q.options.find(o => o.optionText === selectedAnswers[q._id]);
      const score = opt?.score || 0;

      // Aggregate for sub-category 1
      if (q.subCategory === sub1 && q.facet && q.facet !== 'All') {
        if (!facetData1[q.facet]) facetData1[q.facet] = { sum: 0, count: 0 };
        facetData1[q.facet].sum += score;
        facetData1[q.facet].count += 1;
      }

      // Aggregate for sub-category 2
      if (q.subCategory === sub2 && q.facet && q.facet !== 'All') {
        if (!facetData2[q.facet]) facetData2[q.facet] = { sum: 0, count: 0 };
        facetData2[q.facet].sum += score;
        facetData2[q.facet].count += 1;
      }
    }
  });

  // Debug: check if we actually collected facet data
  console.log("Facet data for", sub1, ":", facetData1);
  console.log("Facet data for", sub2, ":", facetData2);

  // Radar data — average score out of 5 per facet
  const radarDataSub1 = {
    labels: Object.keys(facetData1),
    datasets: [{
      label: sub1 || "Sub-category 1",
      data: Object.values(facetData1).map(f => Number((f.sum / f.count).toFixed(1))),
      backgroundColor: '#9436eb40',
      borderColor: '#9436eb',
      borderWidth: 2.5,
      pointBackgroundColor: '#fff',
      pointBorderColor: '#9436eb',
      pointRadius: 4,
    }]
  };

  const radarDataSub2 = {
    labels: Object.keys(facetData2),
    datasets: [{
      label: sub2 || "Sub-category 2",
      data: Object.values(facetData2).map(f => Number((f.sum / f.count).toFixed(1))),
      backgroundColor: '#f8872a45',
      borderColor: '#f8872a',
      borderWidth: 2.5,
      pointBackgroundColor: '#fff',
      pointBorderColor: '##f8872a',
      pointRadius: 4,
    }]
  };

 const radarOptions = {
    scales: {
      r: {
        angleLines: {
          display: true,
          color: '#3b3b3b',           // light grey for radial lines
        },
        grid: {
          color: '#3b3b3b',           // light grey for concentric circles
          lineWidth: 1,
        },
        ticks: {
          stepSize: 1,
          min: 0,
          max: 5,
          backdropColor: 'transparent',
          color: '#9f9f9f',              // tick labels color
          font: { size: 12 }
        },
        pointLabels: {
          font: { size: 13, weight: '500' },
          padding: 20,
          color: '#9f9f9f'
        },
        suggestedMin: 0,
        suggestedMax: 5,
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `${context.label}: ${context.raw}/5`
        }
      }
    },
    maintainAspectRatio: false,
    responsive: true
  };

  return (
    <div className="specific-page-intro-screen">
      <div className="intro-content break-content" style={{ maxWidth: '1200px', padding: '2rem 1rem' }}>
        <div style={{ marginTop: '-100px'}} className="admin-dashboard-logo intro-screen logo-career"></div>

        <h2 style={{ marginTop: '50px'}} className="intro-heading-great-job">Outstanding Work!</h2>

        <p className="intro-text break-text" style={{ marginBottom: '2.5rem', fontSize: '1.25rem' }}>
          You've completed <strong>2 out of 5 sub-categories</strong>!<br />
          <strong>Take a break</strong><br />
            You can come back anytime to continue.
        </p>

        {/* Side-by-side radars */}
       <div className='radar-graph-career' style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '3rem',
          flexWrap: 'wrap',
          marginTop: '50px',
          width: '1200px',
        }}>
          {/* Radar 1 */}
          <div style={{ flex: '1', minWidth: '340px', maxWidth: '480px', height: '420px' }}>
            <h3 style={{ textAlign: 'center', marginBottom: '1rem', color: '#9436eb', fontSize: '1.4rem' }}>
              {sub1 || 'Sub-category 1'}
            </h3>
            <div style={{ height: '360px' }}>
              {Object.keys(facetData1).length > 0 ? (
                <Radar data={radarDataSub1} options={radarOptions} />
              ) : (
                <p style={{ textAlign: 'center', color: '#777', paddingTop: '140px' }}>
                  No facet data collected for this sub-category.
                </p>
              )}
            </div>
          </div>

          {/* Radar 2 */}
          <div style={{ flex: '1', minWidth: '340px', maxWidth: '480px', height: '420px' }}>
            <h3 style={{ textAlign: 'center', marginBottom: '1rem', color: '#f8872a', fontSize: '1.4rem' }}>
              {sub2 || 'Sub-category 2'}
            </h3>
            <div style={{ height: '360px' }}>
              {Object.keys(facetData2).length > 0 ? (
                <Radar data={radarDataSub2} options={radarOptions} />
              ) : (
                <p style={{ textAlign: 'center', color: '#777', paddingTop: '140px' }}>
                  No facet data collected for this sub-category.
                </p>
              )}
            </div>
          </div>
        </div>


        <div className="break-buttons break-buttons-career" style={{ justifyContent: 'center', gap: '2.5rem' }}>
          <button
            className="nav-button continue-button"
            onClick={handleContinueAfterMid}
            style={{ minWidth: '180px' }}
          >
            Continue Now
          </button>

          <button
            className="nav-button save-exit-button"
            onClick={handleSaveExitFromMid}
            style={{ minWidth: '180px' }}
          >
            Save & Exit
          </button>
        </div>

      </div>
    </div>
  );
}

  return (
    <div className="specific-page-career">
      <header className="assessment-header assessment-header-career">
<div className="admin-dashboard-logo intro-screen logo-study"></div>        
<h1 className="assessment-heading">ASPIRE FutureReady Psychometric Test</h1>
        <p className="copyright">© 2026 Oxbridge Gateway Ltd., All Rights Reserved.</p>
                        <button className="nav-button-save nav-button-save-career" onClick={handleSaveProgress}>
                    <FaSave className="save-icon" />
                  </button>
      </header>

      <div className="assessment-container">
        <div className="progress-container">
          <p>Total Progress: {answeredCount} / {allQuestions.length} questions</p>
          <div className="progress-bar">
            <div
              className="progress-fill total-progress"
              style={{ width: `${(answeredCount / allQuestions.length) * 100}%` }}
            ></div>
          </div>
        </div>

        <div className="assessment-question-container">
          <h3 className="category-text">Category: {currentQuestion.category}</h3>
          <p className="category-text-progress">
            Progress: {answeredCountByCategory[currentQuestion.category] || 0} /
            {allQuestions.filter((q) => q.category === currentQuestion.category).length} questions
          </p>
          <p className="category-text-career">{currentQuestion.subCategory}</p>
          <p className="assessment-question-text">{currentQuestion.questionText}</p>

          {currentQuestion.exampleInfo && (
            <div className="example-info-container">
              <button
                className="example-info-toggle"
                onClick={toggleExampleDropdown}
                aria-expanded={isExampleOpen}
                aria-controls="example-info-content"
              >
                {isExampleOpen ? 'Hide Example' : 'Show Example'}
              </button>
              <div
                id="example-info-content"
                className={`example-info-content ${isExampleOpen ? 'open' : ''}`}
              >
                {isExampleOpen && <p>{currentQuestion.exampleInfo}</p>}
              </div>
            </div>
          )}

          <div className="navigation-container-learning">
              <div className="options-navigation">
                <button
                  className={`nav-arrow nav-arrow-left ${showPreviousButton ? 'visible' : ''}`}
                  onClick={handlePrevious}
                  aria-label="Previous question"
                  disabled={!showPreviousButton}
                >
                  <svg viewBox="0 0 24 24" className="arrow-icon">
                    <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </button>
                <div className="options-container">
                  {currentQuestion.options.map((option, index) => (
                    <button
                      key={index}
                      className={`option-button ${
                        currentSelectedAnswer === option.optionText ? 'selected' : ''
                      }`}
                      onClick={() => handleAnswerSelect(option.optionText)}
                    >
                      {option.optionText}
                    </button>
                  ))}
                </div>
                <button
                  className={`nav-arrow nav-arrow-right-learning ${
                    currentAllIndex < allQuestions.length - 1 ? 'visible' : ''
                  }`}
                  onClick={handleNext}
                  aria-label="Next question"
                  disabled={currentAllIndex >= allQuestions.length - 1}
                >
                  <svg viewBox="0 0 24 24" className="arrow-icon">
                    <path d="M9 18l-6-6 6-6" fill="none" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </button>
              </div>
            <div className="action-buttons">
              {unansweredCount === 0 && (
                <button className="nav-button" onClick={handleAnswerSubmit}>
                  Submit
                </button>
              )}
            </div>
          </div>
          <button className="return-dashboard-cross return-dashboard-cross-career" onClick={handleReturnToDashboard}>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CareerAssessment;